import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, CheckCircle2, XCircle, Clock, KeyRound, ShieldCheck,
  LayoutList, Grid, MapPin, Phone, Search
} from "lucide-react";
import { motion } from "framer-motion";
import { api, asItems, formatApiError, prefixUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

const formatDateTime = (dtStr) => {
  if (!dtStr) return "—";
  try {
    const d = new Date(dtStr);
    return d.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + " · " + d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dtStr;
  }
};

const STATUS_COLORS = {
  pending: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
  requested: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
  scheduled: "bg-primary/10 text-primary",
  in_progress: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/10 text-destructive",
};

const STATUS_TABS = [
  { id: "all", label: "All Visits" },
  { id: "pending", label: "Pending" },
  { id: "scheduled", label: "Scheduled" },
  { id: "in_progress", label: "In progress" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

const normalizedStatus = (status) => {
  const value = String(status || "PENDING").toLowerCase();
  if (value === "confirmed") return "scheduled";
  if (value === "visited") return "completed";
  if (value === "requested") return "pending";
  return value;
};

const STATUS_STEPS = ["pending", "scheduled", "in_progress", "completed"];

function VisitTimeline({ status }) {
  const current = normalizedStatus(status);
  if (["cancelled", "no_show"].includes(current)) {
    return <div className="mt-3 rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs font-semibold text-destructive">{current === "no_show" ? "Marked as no show" : "Visit cancelled"}</div>;
  }
  const activeIndex = Math.max(0, STATUS_STEPS.indexOf(current));
  return <ol className="mt-3 grid grid-cols-4 gap-1" aria-label="Site visit progress">
    {STATUS_STEPS.map((step, index) => <li key={step} className="min-w-0">
      <div className={`h-1.5 rounded-full ${index <= activeIndex ? "bg-primary" : "bg-muted"}`} />
      <span className={`mt-1 block truncate text-[9px] font-bold uppercase tracking-wide ${index <= activeIndex ? "text-foreground" : "text-muted-foreground"}`}>{step.replace("_", " ")}</span>
    </li>)}
  </ol>;
}

export default function Appointments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  // Scope controls only the visible tab/request alias. The backend derives the
  // actual row scope from the cookie-authenticated role and ignores widening.
  const [scope, setScope] = useState(
    ["buyer", "owner"].includes(user?.role) ? "mine"
      : user?.role === "agent" ? "agent"
      : user?.role === "office" ? "office"
      : "all"
  );
  
  // Rich panel filtering states
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list"); // "list" or "grid"

  // OTP modal state
  const [verifyTarget, setVerifyTarget] = useState(null); // appointment id
  const [verifyOtp, setVerifyOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  // Buyer Start OTP Modal Popup State
  const [startOtpTarget, setStartOtpTarget] = useState(null);
  const [activeStartOtp, setActiveStartOtp] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  useEffect(() => {
    if (!otpExpiresAt) return;
    const updateCountdown = () => {
      const diff = Math.max(0, Math.floor((new Date(otpExpiresAt).getTime() - Date.now()) / 1000));
      setCountdownSeconds(diff);
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [otpExpiresAt]);

  const load = () => {
    setLoading(true);
    setError("");
    api.get(`/appointments?scope=${scope}`)
      .then(({ data }) => setItems(asItems(data)))
      .catch((requestError) => {
        setItems([]);
        setError(formatApiError(requestError));
      })
      .finally(() => setLoading(false));
  };
  
  useEffect(load, [scope]);

  const transition = async (id, status) => {
    setBusy(true);
    try {
      await api.post(`/appointments/${id}/transition`, { status }); 
      toast.success(`Marked ${status}`); 
      setCancelTarget(null);
      load(); 
    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Action failed"); 
    } finally { setBusy(false); }
  };

  const startOtp = async (id, item, force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${id}/start-verification`, { force_regenerate: force });
      if (data?.otp) {
        setActiveStartOtp(data.otp);
        setOtpExpiresAt(data.expires_at || null);
        setStartOtpTarget(item || { appointment_id: id });
        toast.success(force ? "New OTP code generated" : "OTP Code active");
      } else {
        toast.success("Start code sent to your private notifications.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate code");
    } finally { setBusy(false); }
  };

  const submitVerify = async () => {
    if (!verifyTarget) return;
    setBusy(true);
    try {
      await api.post(`/appointments/${verifyTarget}/verify-visit`, { otp: verifyOtp.trim() });
      toast.success("Visit verified");
      setVerifyTarget(null);
      setVerifyOtp("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const canManage = ["agent", "office", "admin", "super_admin"].includes(user?.role);
  const isBuyer = ["buyer", "owner"].includes(user?.role);

  // Filter items locally by status and search query
  const filteredItems = items.filter(a => {
    const matchesStatus = activeStatusTab === "all" || normalizedStatus(a.status) === activeStatusTab;
    
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = query === "" ||
      String(a.property?.title || "").toLowerCase().includes(query) ||
      String(a.property?.city || "").toLowerCase().includes(query) ||
      String(a.visitor_name || "").toLowerCase().includes(query) ||
      String(a.agent_name || "").toLowerCase().includes(query);
      
    return matchesStatus && matchesSearch;
  });

  return (
    <div data-testid="appointments-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Appointments</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-primary">Property Visit Schedule</h1>
        </div>
        
        {/* Scope selector on the right */}
        {canManage && (
          <Tabs value={scope} onValueChange={setScope} className="shrink-0">
            <TabsList className="bg-secondary/60 border border-border/80 p-1 rounded-xl">
              {user?.role === "agent" && <TabsTrigger value="agent" className="text-xs font-bold rounded-lg px-3 py-1.5">Assigned to me</TabsTrigger>}
              {user?.role === "office" && <TabsTrigger value="office" className="text-xs font-bold rounded-lg px-3 py-1.5">My office</TabsTrigger>}
              {["admin", "super_admin"].includes(user?.role) && <TabsTrigger value="all" className="text-xs font-bold rounded-lg px-3 py-1.5">All</TabsTrigger>}
              <TabsTrigger value="mine" className="text-xs font-bold rounded-lg px-3 py-1.5">Booked by me</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Dynamic Status Tabs & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="w-full lg:w-auto bg-card border border-border/80 p-1 rounded-xl overflow-x-auto scrollbar-none relative flex gap-1 shrink-0">
          {STATUS_TABS.map(tab => {
            const isActive = activeStatusTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveStatusTab(tab.id)}
                className={`flex-1 lg:flex-none text-center px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                  isActive ? "text-white" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`visit-status-tab-${tab.id}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeVisitTab"
                    className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                  isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
                }`}>
                  {items.filter(a => tab.id === "all" || normalizedStatus(a.status) === tab.id).length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search & Layout Toggle */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by property, agent or buyer..."
              className="pl-10 pr-4 py-2 rounded-xl text-sm w-full bg-background border-border"
            />
            <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-3" />
          </div>

          <div className="flex items-center gap-1 bg-card border border-border p-1 rounded-xl h-10 w-full sm:w-auto justify-center">
            <span className="text-[10px] uppercase font-bold text-muted-foreground/60 mr-2 sm:hidden pl-2">Layout View:</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === "list" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"
                }`}
                title="List View"
                aria-label="Use list view"
                aria-pressed={viewMode === "list"}
              >
                <LayoutList className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === "grid" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"
                }`}
                title="Grid View"
                aria-label="Use grid view"
                aria-pressed={viewMode === "grid"}
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3" aria-label="Loading site visits">
          {[0, 1, 2].map((item) => <div key={item} className="h-40 rounded-2xl border border-border shimmer" />)}
        </div>
      ) : error ? (
        <Card className="border border-destructive/30 bg-destructive/5 rounded-2xl py-12 px-6 text-center" role="alert">
          <p className="font-bold text-primary">Site visits could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" variant="outline" className="mt-4" onClick={load}>Try again</Button>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="border border-dashed border-border rounded-2xl py-16 text-center bg-card">
          <span className="inline-flex h-16 w-16 mx-auto mb-3 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary animate-float-soft"><Calendar className="w-8 h-8"/></span>
          <p className="text-muted-foreground font-semibold">No appointments found matching filters.</p>
        </Card>
      ) : viewMode === "list" ? (
        <div className="space-y-3">
          {filteredItems.map(a => {
            const propLink = a.property_id ? `/properties/${a.property_id}` : a.property?.property_id ? `/properties/${a.property.property_id}` : null;
            const visitStatus = normalizedStatus(a.status);
            return (
              <Card key={a.appointment_id} className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4 bg-card border border-border rounded-2xl hover:shadow-md transition duration-200" data-testid={`appt-${a.appointment_id}`}>
                
                {/* Photo Thumbnail */}
                {propLink ? (
                  <Link to={propLink} className="w-full sm:w-24 h-40 sm:h-24 rounded-xl bg-secondary overflow-hidden shrink-0 block group relative shadow-inner" data-testid={`appt-thumb-${a.appointment_id}`}>
                    {a.property?.images?.[0] ? (
                      <img src={getImageUrl(a.property.images[0])} alt={a.property?.title || "Property"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs bg-muted">No Photo</div>
                    )}
                  </Link>
                ) : (
                  <div className="w-full sm:w-24 h-40 sm:h-24 rounded-xl bg-muted shrink-0 flex items-center justify-center text-muted-foreground text-xs shadow-inner">
                    No Photo
                  </div>
                )}

                {/* Details Area */}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[visitStatus] || STATUS_COLORS.pending}`} data-testid={`appt-status-${a.appointment_id}`}>
                        {visitStatus.replace("_", " ")}
                      </span>
                      <span className="text-[10px] font-mono bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded">
                        Ref: {String(a.appointment_id || a.id || "unavailable").slice(0, 8)}
                      </span>
                    </div>

                    {propLink ? (
                      <Link to={propLink} className="font-display font-black text-lg text-primary leading-snug hover:underline block truncate mt-1" data-testid={`appt-title-${a.appointment_id}`}>
                        {a.property?.title || "Property Listing"}
                      </Link>
                    ) : (
                      <div className="font-display font-black text-lg text-primary leading-snug mt-1 truncate">
                        {a.property?.title || "Property Listing"}
                      </div>
                    )}
                    <VisitTimeline status={visitStatus} />

                    {a.property?.address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                        <span className="truncate">{[a.property?.address, a.property?.city].filter(Boolean).join(", ")}</span>
                      </div>
                    )}
                  </div>

                  {/* Visitor details & Time */}
                  <div className="text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2.5 border-t border-dashed border-border/80 mt-2.5">
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">Visitor: <span className="font-semibold text-foreground">{a.visitor_name || "—"}</span> · <span className="text-muted-foreground">{a.visitor_phone || "—"}</span></p>
                      
                      {/* Enriched Agent contact details */}
                      {a.agent_name && (
                        <div className="flex items-center gap-2 text-muted-foreground font-medium flex-wrap">
                          <span>Agent: <strong className="text-foreground">{a.agent_name}</strong></span>
                          {a.agent_phone && (
                            <a 
                              href={`tel:${a.agent_phone}`}
                              className="inline-flex items-center gap-1 text-blue-400 bg-blue-500/5 px-2 py-0.5 border border-blue-500/10 rounded-md hover:bg-blue-500/10 transition"
                              title={`Call Agent ${a.agent_name}`}
                            >
                              <Phone className="w-3 h-3 text-blue-400 shrink-0" />
                              <span>{a.agent_phone}</span>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="flex items-center gap-2 text-foreground font-medium bg-secondary/35 py-1 px-2.5 rounded-md w-fit">
                        <Clock className="w-3.5 h-3.5 text-primary/70 shrink-0"/>
                        <span>{a.date} · {a.time_slot}</span>
                      </div>

                      {a.visit_verified_at && (
                        <div className="text-[10px] text-[hsl(var(--success))] flex items-center gap-1 bg-[hsl(var(--success))]/10 py-0.5 px-2 rounded border border-[hsl(var(--success))]/20 font-semibold mt-1">
                          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                          <span>Verified at {formatDateTime(a.visit_verified_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-row sm:flex-col sm:justify-center justify-end items-center sm:items-stretch gap-2 shrink-0 self-stretch sm:self-center mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/60">
                  {/* Buyer: cancel + generate OTP for visit */}
                  {isBuyer && !["completed", "cancelled", "no_show", "in_progress"].includes(visitStatus) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => startOtp(a.appointment_id, a)} className="gap-1.5 font-bold shadow-sm w-full sm:w-auto" data-testid={`get-otp-${a.appointment_id}`}>
                        <KeyRound className="w-3.5 h-3.5" /> Start OTP
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)} className="gap-1.5 font-bold text-destructive hover:text-destructive hover:bg-destructive/10 w-full sm:w-auto" data-testid={`cancel-${a.appointment_id}`}>
                        <XCircle className="w-3.5 h-3.5"/> Cancel
                      </Button>
                    </>
                  )}

                  {/* Agent / Office: confirm + verify-visit */}
                  {canManage && visitStatus === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => transition(a.appointment_id, "confirmed")} className="gap-1.5 font-bold shadow-sm w-full sm:w-auto" data-testid={`confirm-${a.appointment_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5"/> Confirm
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)} className="gap-1.5 font-bold text-destructive hover:text-destructive hover:bg-destructive/10 w-full sm:w-auto" data-testid={`cancel-${a.appointment_id}`}>
                        <XCircle className="w-3.5 h-3.5"/> Cancel
                      </Button>
                    </>
                  )}
                  {canManage && visitStatus === "scheduled" && (
                    <Button size="sm" onClick={() => { setVerifyTarget(a.appointment_id); setVerifyOtp(""); }} className="gap-1.5 font-bold w-full sm:w-auto" data-testid={`verify-${a.appointment_id}`}>
                      <ShieldCheck className="w-3.5 h-3.5"/> Verify visit
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredItems.map(a => {
            const propLink = a.property_id ? `/properties/${a.property_id}` : a.property?.property_id ? `/properties/${a.property.property_id}` : null;
            const visitStatus = normalizedStatus(a.status);
            return (
              <Card key={a.appointment_id} className="overflow-hidden border border-border bg-card rounded-2xl flex flex-col justify-between hover:shadow-md transition duration-200" data-testid={`appt-grid-card-${a.appointment_id}`}>
                <div>
                  {/* Photo Thumbnail */}
                  <div className="w-full h-40 bg-secondary relative overflow-hidden group">
                    {a.property?.images?.[0] ? (
                      <img src={getImageUrl(a.property.images[0])} alt={a.property?.title || "Property"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs bg-muted">No Photo</div>
                    )}
                    <div className="absolute top-3 left-3 z-10">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm ${STATUS_COLORS[visitStatus] || STATUS_COLORS.pending}`}>
                        {visitStatus.replace("_", " ")}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 z-10 bg-slate-900/80 text-white px-2 py-0.5 rounded text-[9px] font-mono border border-border/40">
                      Ref: {String(a.appointment_id || a.id || "unavailable").slice(0, 8)}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 space-y-3.5">
                    <div>
                      {propLink ? (
                        <Link to={propLink} className="font-display font-bold text-lg text-primary hover:underline line-clamp-1 leading-snug">
                          {a.property?.title || "Property Listing"}
                        </Link>
                      ) : (
                        <span className="font-display font-bold text-lg text-primary block truncate leading-snug">
                          {a.property?.title || "Property Listing"}
                        </span>
                      )}
                      <VisitTimeline status={visitStatus} />
                      {a.property?.address && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <MapPin className="w-3.5 h-3.5 shrink-0 text-primary/70" />
                          <span className="truncate">{[a.property?.address, a.property?.city].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                    </div>

                    {/* Visitor details & Time */}
                    <div className="pt-3 border-t border-dashed border-border/80 space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium bg-secondary/35 py-1.5 px-3 rounded-xl w-fit">
                        <Clock className="w-4 h-4 text-primary/70 shrink-0"/>
                        <span>{a.date} · {a.time_slot}</span>
                      </div>
                      <p className="text-xs text-muted-foreground font-medium">
                        Visitor: <strong className="text-foreground">{a.visitor_name || "—"}</strong>
                        {a.visitor_phone && <span className="ml-1 text-muted-foreground">({a.visitor_phone})</span>}
                      </p>
                    </div>

                    {/* Enriched Agent contact details */}
                    {a.agent_name && (
                      <div className="pt-3 border-t border-dashed border-border/80 flex items-center justify-between gap-2 text-xs">
                        <span className="text-muted-foreground font-medium">
                          Agent: <strong className="text-foreground">{a.agent_name}</strong>
                        </span>
                        {a.agent_phone && (
                          <a 
                            href={`tel:${a.agent_phone}`}
                            className="p-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-450 hover:bg-blue-500/20 rounded-xl transition shadow-sm"
                            title={`Call Agent ${a.agent_name}`}
                          >
                            <Phone className="w-4 h-4 text-blue-400" />
                          </a>
                        )}
                      </div>
                    )}

                    {a.visit_verified_at && (
                      <div className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 py-1 px-2.5 rounded-lg w-full flex items-center gap-1.5 font-semibold">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        <span>Verified at {formatDateTime(a.visit_verified_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions footer */}
                <div className="p-4 border-t border-border flex items-center gap-2 bg-secondary/5">
                  {/* Buyer actions */}
                  {isBuyer && !["completed", "cancelled", "no_show", "in_progress"].includes(visitStatus) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => startOtp(a.appointment_id, a)} className="flex-1 gap-1 font-bold shadow-sm" data-testid={`get-otp-grid-${a.appointment_id}`}>
                        <KeyRound className="w-3.5 h-3.5" /> Start OTP
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)} className="gap-1 font-bold text-destructive hover:bg-destructive/10" data-testid={`cancel-grid-${a.appointment_id}`}>
                        <XCircle className="w-3.5 h-3.5"/> Cancel
                      </Button>
                    </>
                  )}

                  {/* Agent / Office actions */}
                  {canManage && visitStatus === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => transition(a.appointment_id, "confirmed")} className="flex-1 gap-1 font-bold shadow-sm" data-testid={`confirm-grid-${a.appointment_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5"/> Confirm
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)} className="gap-1 font-bold text-destructive hover:bg-destructive/10" data-testid={`cancel-grid-${a.appointment_id}`}>
                        <XCircle className="w-3.5 h-3.5"/> Cancel
                      </Button>
                    </>
                  )}
                  {canManage && visitStatus === "scheduled" && (
                    <Button size="sm" onClick={() => { setVerifyTarget(a.appointment_id); setVerifyOtp(""); }} className="w-full gap-1.5 font-bold" data-testid={`verify-grid-${a.appointment_id}`}>
                      <ShieldCheck className="w-3.5 h-3.5"/> Verify Visit
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Buyer Start OTP Modal Popup */}
      <Dialog open={Boolean(startOtpTarget)} onOpenChange={(val) => { if (!val) setStartOtpTarget(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="start-otp-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 text-primary">
              <KeyRound className="w-6 h-6 text-amber-500" /> Site Visit Start OTP
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Present this 6-digit verification code to the assigned agent when starting your site visit.
            </DialogDescription>
          </DialogHeader>

          {/* Confidential OTP Security Warning Banner */}
          <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3.5 text-xs text-amber-900 dark:text-amber-300 font-medium space-y-1">
            <p className="font-bold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
              Confidential OTP Warning
            </p>
            <p className="text-[11px] opacity-90">
              Share this code ONLY with the assigned agent in person at the property location. Do not share over phone, email, or chat.
            </p>
          </div>

          {/* Styled OTP Display Box */}
          <div className="my-2 bg-secondary/50 border border-border rounded-2xl p-5 text-center space-y-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Your 6-Digit OTP Code</p>
            <div className="font-mono text-4xl font-black tracking-[0.4em] text-primary select-all py-1">
              {activeStartOtp || "••••••"}
            </div>
            <p className="text-xs text-muted-foreground font-semibold flex items-center justify-center gap-1">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              Expires in: <span className="text-rose-500 font-bold tabular-nums">{Math.floor(countdownSeconds / 60)}m {String(countdownSeconds % 60).padStart(2, "0")}s</span>
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(activeStartOtp);
                toast.success("OTP code copied to clipboard!");
              }}
              className="font-bold gap-2 text-xs"
              data-testid="copy-otp-btn"
            >
              Copy Code
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => startOtp(startOtpTarget.appointment_id || startOtpTarget.id || startOtpTarget._id, startOtpTarget, true)}
              className="font-bold gap-2 text-xs"
              data-testid="regenerate-otp-btn"
            >
              Regenerate OTP
            </Button>
            <Button
              type="button"
              onClick={() => setStartOtpTarget(null)}
              className="font-bold text-xs"
              data-testid="close-otp-modal-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-visit-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this site visit?</DialogTitle>
            <DialogDescription>
              The visit request for {cancelTarget?.property?.title || "this property"} will be cancelled. This action cannot be undone from this screen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setCancelTarget(null)}>Keep visit</Button>
            <Button type="button" variant="destructive" disabled={busy} onClick={() => transition(cancelTarget.appointment_id, "cancelled")}>{busy ? "Cancelling…" : "Cancel visit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent verifies OTP */}
      <Dialog open={!!verifyTarget} onOpenChange={(o) => !o && setVerifyTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="otp-verify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5"/>Verify Start Visit OTP</DialogTitle>
            <DialogDescription>
              Enter the 6-digit **Start Visit Verification OTP** shown on the buyer&apos;s phone to authorize and start the walk-through.
            </DialogDescription>
          </DialogHeader>
          <input
            value={verifyOtp}
            onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            maxLength={6}
            className="w-full border border-border rounded-md px-3 py-3 text-center text-2xl tracking-[0.3em] tabular-nums font-bold bg-background focus:outline-none focus:border-primary"
            data-testid="otp-input"
            autoFocus
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setVerifyTarget(null)} data-testid="otp-verify-cancel">Cancel</Button>
            <Button onClick={submitVerify} disabled={busy || verifyOtp.length !== 6} className="font-bold" data-testid="otp-verify-confirm">
              {busy ? "Verifying…" : "Verify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
